import { Component, Input, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { NgbActiveModal, NgbModal, NgbProgressbarModule, NgbTooltipModule } from '@ng-bootstrap/ng-bootstrap';
import {
	BATCH_EDIT_FIELDS,
	BatchEditField,
	BatchEditFieldEnumValue,
	BatchEditOperation,
	BatchEditReport,
	BatchEditService,
	IfThenCondition,
	PlannedChange,
	PreviewPointRow,
	WdFilterComponent,
	WhereCondition,
	WorkflowEditorComponent,
	WorkflowService,
	heutigesDatum,
	kalenderOeffnen
} from '@luzula/luzula-lib';
import { ToastrService } from 'ngx-toastr';
import { AufnahmePunktDocument, isM, istPunktartMitKurzprotokoll, istPunktartMitProtokoll } from '../../data-model';
import { DataService } from '../../data.service';
import { FilterService } from '../../filter.service';
import { BestandBatchPlan, BestandBatchRow, planBestandBatch, previewBestandBatch, runBestandBatch } from '../bestand/bestand-batch';
import { ProjectService } from '../../project.service';
import { buildKartiererBatchField, kartiererOptionsFromProject } from './kartierer-ownership';
import {
	MOOS_GENERA_PRESET,
	SCHICHT_FOR_ART_FIELD_KEY,
	buildStoreEnumOverrides,
	compileArtPattern,
	rewriteSchichtForArtSkipReasons,
	setActiveArtPattern
} from './bohrpunkt-batch-edit-fields';
import { BatchWmsProgressComponent } from './batch-wms-progress.component';
import { parseReplaceRules } from './parse-replace-rules';

export { parseReplaceRules } from './parse-replace-rules';

export type BatchSource = 'all' | 'selection' | 'bounds' | 'filter';

interface SourceOption {
	id: BatchSource;
	label: string;
	available: boolean;
	count: number;
}

type QuickFilter = 'all' | 'lang' | 'kurz' | 'moor' | 'mineral';

interface QuickFilterOption {
	id: QuickFilter;
	label: string;
	/** Punkt soll einbezogen werden, wenn predicate true ist. `null` = alle einbeziehen. */
	predicate: ((p: AufnahmePunktDocument) => boolean) | null;
}

type WizardStep = 'auswahl' | 'felder' | 'operationen' | 'preview' | 'ausfuehrung' | 'abgeschlossen';

type OpKind = BatchEditOperation['kind'];

/** UI-seitige Operator-Liste — muss zur Discriminated Union `WhereCondition` in der Lib passen. */
export type ConditionOpUi =
	| 'eq' | 'ne'
	| 'contains' | 'notContains'
	| 'lt' | 'lte' | 'gt' | 'gte'
	| 'between'
	| 'empty' | 'nonEmpty';

/**
 * UI-Modell fuer eine Cross-Field-Bedingung. Bei op='empty'|'nonEmpty' werden value/value2
 * ignoriert; bei 'between' ist value2 die Obergrenze; sonst ist nur value relevant.
 */
export interface WhereConditionConfig {
	field: string;
	op: ConditionOpUi;
	value?: string;
	value2?: string;
}

/**
 * Operator-Set pro Feld-Typ — analog zum Hauptfilter (filter/operators.ts).
 * String bekommt `contains`/`notContains`, number/date zusätzlich Vergleich + between.
 * boolean/enum bleibt auf eq/ne/empty/nonEmpty beschränkt (Substring/Ordnung sinnlos).
 */
const CONDITION_OPS_BY_TYPE: Record<string, { value: ConditionOpUi; label: string }[]> = {
	string: [
		{ value: 'eq',          label: '= ist gleich' },
		{ value: 'ne',          label: '≠ ist ungleich' },
		{ value: 'contains',    label: 'enthält' },
		{ value: 'notContains', label: 'enthält nicht' },
		{ value: 'empty',       label: 'leer ist' },
		{ value: 'nonEmpty',    label: 'nicht leer ist' }
	],
	number: [
		{ value: 'eq',       label: '= ist gleich' },
		{ value: 'ne',       label: '≠ ist ungleich' },
		{ value: 'lt',       label: '< kleiner als' },
		{ value: 'lte',      label: '≤ kleiner gleich' },
		{ value: 'gt',       label: '> größer als' },
		{ value: 'gte',      label: '≥ größer gleich' },
		{ value: 'between',  label: 'liegt zwischen' },
		{ value: 'empty',    label: 'leer ist' },
		{ value: 'nonEmpty', label: 'nicht leer ist' }
	],
	date: [
		{ value: 'eq',       label: '= ist gleich' },
		{ value: 'ne',       label: '≠ ist ungleich' },
		{ value: 'lt',       label: '< früher als' },
		{ value: 'lte',      label: '≤ am oder vor' },
		{ value: 'gt',       label: '> später als' },
		{ value: 'gte',      label: '≥ am oder nach' },
		{ value: 'between',  label: 'liegt zwischen' },
		{ value: 'empty',    label: 'leer ist' },
		{ value: 'nonEmpty', label: 'nicht leer ist' }
	],
	boolean: [
		{ value: 'eq',       label: '= ist gleich' },
		{ value: 'ne',       label: '≠ ist ungleich' },
		{ value: 'empty',    label: 'leer ist' },
		{ value: 'nonEmpty', label: 'nicht leer ist' }
	],
	enum: [
		{ value: 'eq',       label: '= ist gleich' },
		{ value: 'ne',       label: '≠ ist ungleich' },
		{ value: 'empty',    label: 'leer ist' },
		{ value: 'nonEmpty', label: 'nicht leer ist' }
	]
};

const FALLBACK_OPS = CONDITION_OPS_BY_TYPE['string']!;

export function conditionOpsForType(type: string | undefined): { value: ConditionOpUi; label: string }[] {
	return type ? (CONDITION_OPS_BY_TYPE[type] ?? FALLBACK_OPS) : FALLBACK_OPS;
}

/** True wenn der Operator GAR keinen Wert braucht (empty/nonEmpty). */
export function opNeedsNoValue(op: ConditionOpUi | undefined): boolean {
	return op === 'empty' || op === 'nonEmpty';
}

/** True wenn der Operator zwei Werte braucht (between). */
export function opNeedsTwoValues(op: ConditionOpUi | undefined): boolean {
	return op === 'between';
}

/**
 * Ein flaches UI-Modell pro Feld, das in eine `BatchEditOperation` serialisiert wird.
 * Pro Feld wird nur der Anteil ausgefuellt, der zur gewaehlten `kind` passt.
 */
interface OperationConfig {
	kind: OpKind | '';
	onlyIfEmpty: boolean;

	/** Cross-Field-Bedingungen (UND-verknuepft). Operation laeuft nur, wenn alle erfuellt sind. */
	whereConditions: WhereConditionConfig[];

	// Sonder-Eingabe nur fuer SCHICHT_FOR_ART_FIELD_KEY: Pflanzenart-Muster (Pipe-Liste,
	// case-insensitive Substring auf `vegetation`-Feld). Wird in computePreview/runExecute
	// in eine Regex kompiliert und ueber `setActiveArtPattern` an den Accessor uebergeben.
	artPattern?: string;

	// set
	setValue?: string;

	// mapping (als Text A=B pro Zeile)
	mappingText?: string;
	mappingPassthrough: boolean;
	/**
	 * Cache der unzulaessigen Mapping-Ziel-Werte (nur fuer enum-Felder gefuellt).
	 * Wird beim Editieren des `mappingText` einmal aktualisiert; das Template liest
	 * die Liste direkt — kein Methoden-Call pro CD-Pass, kein NG0103.
	 */
	mappingInvalidTargets: string[];

	// replaceList — sequentielle Teilstring-Ersetzung (eine Regel pro Zeile, Format A=B)
	replaceListText?: string;
	/** Wenn true: Suchmuster als Regex (Flag 'g') interpretieren; `to` unterstuetzt $1. */
	replaceListRegex: boolean;

	// stringTransform
	stringTransform?: 'trim' | 'upper' | 'lower' | 'prefix' | 'suffix' | 'replace';
	strArg1?: string;
	strArg2?: string;
	strRegex: boolean;

	// arithmetic
	arithmeticOp?: 'add' | 'sub' | 'mul' | 'set' | 'round';
	arithmeticValue?: number;

	// dateOp
	dateOp?: 'today' | 'addDays' | 'monthStart';
	dateDays?: number;

	// ifThen — Operator-Set parallel zur Where-Bedingung (siehe ConditionOpUi).
	ifCondOp?: ConditionOpUi;
	ifCondValue?: string;
	ifCondValue2?: string;
	ifThenKind?: 'set' | 'clear';
	ifThenValue?: string;
}

/**
 * Konstante Typ-Tags pro BatchEditFieldType — stabile Object-Identitaet, damit
 * `typeTagFor()` im Template kein neues Objekt pro Aufruf liefert (NG0103-Schutz).
 */
const TYPE_TAGS: Record<'string' | 'number' | 'date' | 'boolean' | 'enum',
	{ kind: 'mono' | 'icon'; value: string; title: string }> = {
	string:  { kind: 'mono', value: 'abc', title: 'Text' },
	number:  { kind: 'mono', value: '123', title: 'Zahl' },
	date:    { kind: 'icon', value: 'bi-calendar3', title: 'Datum' },
	boolean: { kind: 'icon', value: 'bi-check2-square', title: 'Ja/Nein' },
	enum:    { kind: 'icon', value: 'bi-chevron-down', title: 'Auswahl' }
};

function makeDefaultConfig(): OperationConfig {
	return {
		kind: '',
		onlyIfEmpty: false,
		whereConditions: [],
		mappingPassthrough: true,
		mappingInvalidTargets: [],
		replaceListRegex: false,
		strRegex: false
	};
}

@Component({
	selector: 'bp-batch-edit-wizard',
	standalone: true,
	imports: [CommonModule, FormsModule, NgbProgressbarModule, NgbTooltipModule],
	templateUrl: './batch-edit-wizard.component.html',
	styleUrls: ['./batch-edit-wizard.component.css']
})
export class BatchEditWizardComponent implements OnInit {
	/**
	 * Vollstaendige Punktmenge fuer den Wizard. Aus dieser Menge werden anhand der
	 * gewaehlten Quelle (`sourceId`) die jeweils relevanten Punkte abgeleitet.
	 */
	@Input() allPoints: AufnahmePunktDocument[] = [];
	/** IDs der derzeit per SelectionService ausgewaehlten Punkte. */
	@Input() selectionIds: Set<string> = new Set();
	/** IDs der Punkte im aktuellen Kartenausschnitt. */
	@Input() boundsIds: Set<string> = new Set();
	/** True, wenn ueberhaupt ein Kartenausschnitt definiert ist. */
	@Input() boundsAvailable = false;
	/** IDs des aktuell aktiven Filters; null = kein Filter aktiv. */
	@Input() filterIds: Set<string> | null = null;
	/** Vorausgewaehlte Quelle (z.B. 'selection', wenn schon Punkte selektiert sind). */
	@Input() initialSourceId: BatchSource = 'all';

	private batchEditService = inject(BatchEditService);
	private modal = inject(NgbActiveModal);
	private modalService = inject(NgbModal);
	private fs = inject(FilterService);
	private router = inject(Router);
	private workflow = inject(WorkflowService);
	private toastr = inject(ToastrService);
	private projectService = inject(ProjectService);
	private ds = inject(DataService);
	private injectedFields = inject(BATCH_EDIT_FIELDS);
	/**
	 * Effektive Feldliste. Basis = statisch registrierte Felder; bei berechtigten
	 * Usern (Owner/Manager/Entwickler) zusätzlich das gekoppelte „Kartierer"-Feld
	 * (Nutzer-Auswahl, setzt zugleich workflow.createdBy). Wird in ngOnInit gebaut.
	 */
	public fields: BatchEditField[] = this.injectedFields;

	step: WizardStep = 'auswahl';

	/**
	 * Statische Beispiel-Strings fuer die Regex-Hilfsbox im Template. Liegen hier statt inline,
	 * weil geschweifte Klammern im Template als Interpolation/ICU geparst werden.
	 */
	readonly regexHelp = {
		tokenSyntax: '{{feldpfad}}',
		tokenWithFilterSyntax: '{{feldpfad|filter}}',
		tokenForstamt: '{{header.forstamt}}',
		tokenRevier: '{{header.revier}}',
		tokenNummer: '{{header.nummer}}',
		tokenForstamtNr: '{{header.forstamt|nr}}',
		tokenRevierNr: '{{header.revier|nr}}',
		exampleReplaceForstamtRevierMitNr: '{{header.forstamt|nr}}-{{header.revier|nr}}-$1-20$2',
		exampleReplaceForstamtRevier: '{{header.forstamt}}-{{header.revier}}-$1-20$2',
		regexJahrSearch: '^(\\d+)-(\\d{2})$',
		quantifierExact: '{n}'
	};

	/**
	 * True, solange der Aufrufer noch Daten lädt. Das Template zeigt dann nur einen
	 * Spinner statt Step 1 — der Modal kann so ohne Verzögerung geöffnet werden,
	 * während `queryIndex(true)` im Hintergrund läuft.
	 */
	loading = true;

	// Step 1: Quellen-Auswahl
	sourceId: BatchSource = 'all';
	sourceOptions: SourceOption[] = [];

	/** Aus der gewaehlten Quelle abgeleitete Punktmenge. Wird in `applySource()` aktualisiert. */
	points: AufnahmePunktDocument[] = [];

	// Step 1: Punkt-Auswahl
	excludedPointIds = new Set<string>();

	// Step 1: Schnellfilter (zusätzlich zur Quellen-Auswahl). Default 'all' = keine weitere
	// Einschränkung. Auswahl setzt `excludedPointIds` neu — anschließendes manuelles Toggeln
	// einzelner Punkte bleibt möglich, ändert aber nicht den Quickfilter-Status.
	quickFilter: QuickFilter = 'all';
	readonly quickFilterOptions: QuickFilterOption[] = [
		{ id: 'all',     label: 'Alle einbeziehen',         predicate: null },
		{ id: 'lang',    label: 'Nur Langprotokoll',        predicate: p => istPunktartMitProtokoll(p.type) },
		{ id: 'kurz',    label: 'Nur Kurzprotokoll',        predicate: p => istPunktartMitKurzprotokoll(p.type) },
		{ id: 'moor',    label: 'Nur Moorpunkte',           predicate: p => isM(p) },
		{ id: 'mineral', label: 'Nur mineralische Bohrpunkte', predicate: p => istPunktartMitProtokoll(p.type) && !isM(p) }
	];

	/** Treffer-Anzahl pro Quickfilter — fuer Badges am Button. Wird bei Quellenwechsel neu berechnet. */
	quickFilterCounts = new Map<QuickFilter, number>();

	// Step 2: Feld-Auswahl
	selectedFieldKeys = new Set<string>();

	// Step 3: Operations-Config pro Feld-Key
	configs = new Map<string, OperationConfig>();

	// Step 4: Preview
	preview: PreviewPointRow[] = [];
	excludedPreviewPointIds = new Set<string>();

	// Step 5: Ausfuehrung
	executing = false;
	progressDone = 0;
	progressTotal = 0;
	currentPointId = '';
	report: BatchEditReport | null = null;

	// Cached derived state — neu berechnet bei jeder relevanten Mutation.
	// Reine Getter im Template würden bei jedem CD-Pass eine neue Array-Identität liefern,
	// was im Dev-Mode NG0103 (Infinite Change Detection) auslöst.
	groupedFields: { group: string; fields: BatchEditField[] }[] = [];
	/** Gefilterte Sicht auf groupedFields — beruecksichtigt fieldFilter (Suchfeld in Step 2). */
	displayedGroupedFields: { group: string; fields: BatchEditField[] }[] = [];
	/** Live-Suchtext des „Feld filtern…"-Inputs in Step 2 (Substring-Match auf Label/Key). */
	fieldFilter = '';
	selectedFieldsList: BatchEditField[] = [];
	selectedPoints: AufnahmePunktDocument[] = [];

	// Zusatz-Operation: Bestand-Texte SEA-konvertieren (äquivalent zum Import).
	bestandKonvertieren = false;
	bestandPlan: BestandBatchPlan | null = null;
	bestandPreviewRows: BestandBatchRow[] = [];
	bestandGespeichert = 0;

	/** Bestand-Kachel auch im Feld-Filter findbar (z.B. „sea", „bestand", „konvert"). */
	get bestandFilterMatch(): boolean {
		const f = this.fieldFilter.trim().toLowerCase();
		return !f || 'bestand sea-konvertieren umschreiben'.includes(f);
	}
	private availableKindsCache = new Map<string, { kind: OpKind; label: string }[]>();

	ngOnInit(): void {
		// Berechtigte User (Owner/Entwickler) bekommen das gekoppelte „Kartierer"-Feld
		// (Nutzer-Auswahl). Es setzt Name UND Eigentum (workflow.createdBy) gemeinsam —
		// ein freier Kartierer-Text ohne passenden Eigentümer ist damit nicht mehr
		// möglich. Für alle anderen entfällt das Feld (Manager dürfen Punktdaten nicht
		// ändern, M2; Bearbeiter dürfen Eigentum nicht verschieben).
		this.fields = this.buildEffectiveFields();

		// Datenunabhaengige Initialisierung — laeuft sofort, damit Step 2 (Felder) ohne
		// Verzoegerung bereit ist, sobald der Lade-Spinner verschwindet.
		this.whereFieldOptions = this.fields.map(f => ({ key: f.key, label: f.label }));
		this.groupedFields = this.computeGroupedFields();
		this.displayedGroupedFields = this.groupedFields;
		this.refreshSelectedFieldsList();
		// Falls `allPoints` direkt als Input gesetzt wurde (Tests / synchroner Aufruf):
		// gleich starten. Im Normalfall ruft der Parent `start()` nach `queryIndex(true)` auf.
		if (this.allPoints.length > 0 && this.loading) {
			this.start();
		}
	}

	/**
	 * Aktiviert den Wizard, sobald der Parent alle Daten gesetzt hat. Berechnet die
	 * Quellen-Optionen, waehlt die initiale Quelle und blendet den Spinner aus.
	 * Aufruf nach Setzen von `allPoints`/`selectionIds`/etc.
	 */
	start(): void {
		this.rebuildSourceOptions();
		this.applySource(this.pickInitialSource());
		this.loading = false;
	}

	/**
	 * Leitet die Punktmenge fuer eine gegebene Quelle aus `allPoints` ab.
	 * Fuer 'all' bleibt die Menge unveraendert; sonst wird per ID-Set gefiltert.
	 */
	private pointsForSource(source: BatchSource): AufnahmePunktDocument[] {
		switch (source) {
			case 'selection': return this.allPoints.filter(p => this.selectionIds.has(p._id));
			case 'bounds':    return this.allPoints.filter(p => this.boundsIds.has(p._id));
			case 'filter':    return this.filterIds ? this.allPoints.filter(p => this.filterIds!.has(p._id)) : [];
			case 'all':
			default:          return [...this.allPoints];
		}
	}

	private rebuildSourceOptions(): void {
		this.sourceOptions = [
			{ id: 'all',       label: 'Alle Punkte',     available: true,                       count: this.allPoints.length },
			{ id: 'selection', label: 'Auswahl',         available: this.selectionIds.size > 0, count: this.selectionIds.size },
			{ id: 'bounds',    label: 'Kartenausschnitt', available: this.boundsAvailable,      count: this.boundsIds.size },
			{ id: 'filter',    label: 'Filter',          available: this.filterIds !== null,    count: this.filterIds?.size ?? 0 }
		];
	}

	/** Waehlt die initiale Quelle: bevorzugt `initialSourceId`, faellt auf 'all' zurueck, falls nicht verfuegbar. */
	private pickInitialSource(): BatchSource {
		const preferred = this.sourceOptions.find(o => o.id === this.initialSourceId);
		if (preferred?.available && preferred.count > 0) return preferred.id;
		return 'all';
	}

	/**
	 * Wechselt die Quelle: setzt `points` neu, verwirft individuelle Exklusionen
	 * und Schnellfilter, und berechnet die Quickfilter-Trefferzahlen neu.
	 */
	applySource(source: BatchSource): void {
		this.sourceId = source;
		this.points = this.pointsForSource(source);
		this.excludedPointIds.clear();
		this.quickFilter = 'all';
		this.computeQuickFilterCounts();
		this.refreshSelectedPoints();
	}

	private computeQuickFilterCounts(): void {
		this.quickFilterCounts.clear();
		for (const opt of this.quickFilterOptions) {
			const n = opt.predicate ? this.points.filter(opt.predicate).length : this.points.length;
			this.quickFilterCounts.set(opt.id, n);
		}
	}

	/**
	 * Oeffnet den globalen Filter-Editor. Nach Anwenden wird der globale FilterService
	 * aktualisiert (wirkt also auch ausserhalb des Wizards weiter), die neue ID-Menge
	 * gegen `allPoints` geschnitten und automatisch als Quelle 'filter' aktiviert.
	 */
	openFilterEditor(): void {
		const modalRef = this.modalService.open(WdFilterComponent, { size: 'lg' });
		modalRef.componentInstance.local = true;
		modalRef.componentInstance.filterApplied.subscribe((filterName: string) => {
			this.fs.get(filterName).then(conditions => {
				if (!conditions) return;
				this.fs.storeViews(filterName, conditions, false, [], () => {
					this.fs.applyFilter(filterName).then(() => {
						this.filterIds = new Set(this.fs.filteredDocIds);
						this.rebuildSourceOptions();
						this.applySource('filter');
					});
				});
			});
		});
	}

	/**
	 * Baut die effektive Feldliste: statische Felder + (nur für Owner/Manager/
	 * Entwickler) das gekoppelte „Kartierer"-Feld aus den Projekt-Mitgliedern.
	 */
	private buildEffectiveFields(): BatchEditField[] {
		// List-gestützte Enums aus der zentralen DB (Store) statt aus dem gebündelten
		// JSON beziehen — so bietet der Batch-Editor genau die Werte an, die auch das
		// Formular akzeptiert, und folgt Editor-Edits. Listen ohne Store-Daten behalten
		// ihren statischen Seed/Fallback aus BOHRPUNKT_BATCH_EDIT_FIELDS.
		// Einmalig in ngOnInit aufgerufen → enumValues-Referenzen bleiben über die
		// Lebensdauer stabil (enumValuesForKey, NG0103-Schutz).
		const overrides = buildStoreEnumOverrides(this.ds);
		const base = Object.keys(overrides).length === 0
			? this.injectedFields
			: this.injectedFields.map(f => overrides[f.key] ? { ...f, enumValues: overrides[f.key] } : f);

		// Kartierer umhängen = Punktdaten-Änderung → nur Owner/Entwickler (Manager
		// dürfen Punktdaten nicht ändern, Matrix M2). Siehe canIReassignKartierer.
		if (!this.projectService.canIReassignKartierer()) return base;
		const members = kartiererOptionsFromProject(this.projectService.currentProjectDoc());
		if (members.length === 0) return base;
		return [...base, buildKartiererBatchField(members)];
	}

	private computeGroupedFields(): { group: string; fields: BatchEditField[] }[] {
		const groups = new Map<string, BatchEditField[]>();
		for (const f of this.fields) {
			const g = f.group ?? 'Sonstiges';
			if (!groups.has(g)) groups.set(g, []);
			groups.get(g)!.push(f);
		}
		return Array.from(groups.entries()).map(([group, fields]) => ({ group, fields }));
	}

	private refreshSelectedPoints(): void {
		this.selectedPoints = this.points.filter(p => !this.excludedPointIds.has(p._id));
	}

	private refreshSelectedFieldsList(): void {
		this.selectedFieldsList = this.fields.filter(f => this.selectedFieldKeys.has(f.key));
	}

	// -------------------------------------------------------------------
	// Derived State
	// -------------------------------------------------------------------

	/** Aktive Preview-Zeilen (nicht excludiert). Stabil pro Render — wird beim Toggle invalidiert. */
	private _activePreview: PreviewPointRow[] = [];
	private _previewChangedCount = 0;

	get activePreview(): PreviewPointRow[] {
		return this._activePreview;
	}

	get previewChangedCount(): number {
		return this._previewChangedCount;
	}

	private refreshActivePreview(): void {
		this._activePreview = this.preview.filter(r => !this.excludedPreviewPointIds.has(r.pointId));
		this._previewChangedCount = this._activePreview.filter(r => r.willChange).length;
	}

	/** Pro ausgewaehltem Feld ist die OperationConfig vollstaendig? */
	get allConfigsValid(): boolean {
		for (const f of this.selectedFieldsList) {
			const cfg = this.configs.get(f.key);
			if (!cfg || !cfg.kind) return false;
			if (!this.isConfigComplete(f, cfg)) return false;
		}
		return true;
	}

	// -------------------------------------------------------------------
	// Step 1: Auswahl
	// -------------------------------------------------------------------

	trackByPointId(_index: number, p: AufnahmePunktDocument): string { return p._id; }

	togglePointExclusion(pointId: string): void {
		if (this.excludedPointIds.has(pointId)) this.excludedPointIds.delete(pointId);
		else this.excludedPointIds.add(pointId);
		this.refreshSelectedPoints();
	}

	includeAllPoints(): void {
		this.excludedPointIds.clear();
		this.quickFilter = 'all';
		this.refreshSelectedPoints();
	}

	/**
	 * Wendet einen Schnellfilter auf die Punktliste an: alle Punkte, die das Prädikat
	 * NICHT erfüllen, werden in `excludedPointIds` aufgenommen. Der bereits vom globalen
	 * Filter vorgefilterte `points`-Bestand bleibt unverändert; manuelle Einzel-Toggles
	 * danach funktionieren wie gewohnt.
	 */
	applyQuickFilter(id: QuickFilter): void {
		this.quickFilter = id;
		const opt = this.quickFilterOptions.find(o => o.id === id);
		this.excludedPointIds.clear();
		if (opt && opt.predicate) {
			for (const p of this.points) {
				if (!opt.predicate(p)) this.excludedPointIds.add(p._id);
			}
		}
		this.refreshSelectedPoints();
	}

	// -------------------------------------------------------------------
	// Step 2: Felder
	// -------------------------------------------------------------------

	toggleField(key: string): void {
		if (this.selectedFieldKeys.has(key)) {
			this.selectedFieldKeys.delete(key);
			this.configs.delete(key);
		} else {
			this.selectedFieldKeys.add(key);
			const cfg = makeDefaultConfig();
			// Sonder-Feld: nur ein einziges sinnvolles kind ('set'); direkt vorbelegen,
			// damit der Operation-Dropdown nicht erst manuell auf den einzigen Eintrag
			// gestellt werden muss.
			if (key === SCHICHT_FOR_ART_FIELD_KEY) cfg.kind = 'set';
			this.configs.set(key, cfg);
		}
		this.refreshSelectedFieldsList();
	}

	/**
	 * Wendet `fieldFilter` (Substring auf label/key, case-insensitive) auf die Gruppen an.
	 * Gruppen ohne Treffer fallen weg, damit der Header nicht alleine stehen bleibt.
	 * Wird per (ngModelChange) am Suchfeld aufgerufen — kein Getter, sonst neue
	 * Array-Identitaet pro CD-Pass und damit NG0103 im verschachtelten *ngFor.
	 */
	onFieldFilterChange(): void {
		const q = this.fieldFilter.trim().toLowerCase();
		if (!q) { this.displayedGroupedFields = this.groupedFields; return; }
		this.displayedGroupedFields = this.groupedFields
			.map(g => ({ group: g.group, fields: g.fields.filter(f =>
				f.label.toLowerCase().includes(q) || f.key.toLowerCase().includes(q)
			) }))
			.filter(g => g.fields.length > 0);
	}

	/**
	 * Kompaktes Typ-Kuerzel fuer die Feld-Auswahl. Ersetzt die fruehere Wort-Pille
	 * ('string'/'number'/...) durch einen kurzen visuellen Marker — entweder Mono-Text
	 * (`abc`/`123`) oder ein bi-Icon (Datum/Boolean/Enum). Reduziert Lese-Lärm,
	 * bleibt aber via `title`-Attribut zugaenglich.
	 *
	 * Stabile Object-Identitaet pro Typ (Konstanten) — sonst wuerde *ngIf den Wert
	 * pro CD-Pass als geaendert sehen und NG0103 ausloesen.
	 */
	typeTagFor(type: BatchEditField['type']): { kind: 'mono' | 'icon'; value: string; title: string } {
		return TYPE_TAGS[type];
	}

	// -------------------------------------------------------------------
	// Step 3: Operationen
	// -------------------------------------------------------------------

	/**
	 * Liefert die verfügbaren Operations-Kinds für ein Feld. Gecacht pro Feld-Key,
	 * weil das Template diese Methode in einem verschachtelten *ngFor aufruft —
	 * würde bei jedem CD-Pass ein neues Array entstehen, gäbe es NG0103.
	 */
	availableKinds(field: BatchEditField): { kind: OpKind; label: string }[] {
		let cached = this.availableKindsCache.get(field.key);
		if (!cached) {
			cached = this.computeAvailableKinds(field);
			this.availableKindsCache.set(field.key, cached);
		}
		return cached;
	}

	private computeAvailableKinds(field: BatchEditField): { kind: OpKind; label: string }[] {
		// Sonderfall: das "Schicht fuer Pflanzenart"-Feld unterstuetzt nur kind='set'.
		// Andere Operationen (clear/mapping/ifThen) ergeben in Kombination mit dem
		// Art-Muster keinen Sinn und wuerden den Accessor verwirren.
		if (field.key === SCHICHT_FOR_ART_FIELD_KEY) {
			return [{ kind: 'set', label: 'Schicht setzen' }];
		}

		const base: { kind: OpKind; label: string }[] = [
			{ kind: 'set', label: 'Wert setzen' },
			{ kind: 'clear', label: 'Feld leeren' }
		];
		const conditional: { kind: OpKind; label: string } = { kind: 'ifThen', label: 'Wenn / Dann' };

		if (field.type === 'string' || field.type === 'enum') {
			const ops: { kind: OpKind; label: string }[] = [
				...base,
				{ kind: 'mapping', label: 'Werte zuordnen (exakter Wert)' },
				conditional
			];
			if (field.type === 'string') {
				ops.push({ kind: 'replaceList', label: 'Mehrfach-Ersetzen (Teilstring)' });
				ops.push({ kind: 'stringTransform', label: 'String-Transformation' });
			}
			return ops;
		}
		if (field.type === 'number') {
			return [...base, { kind: 'arithmetic', label: 'Rechen-Operation' }, conditional];
		}
		if (field.type === 'date') {
			return [...base, { kind: 'dateOp', label: 'Datum-Operation' }, conditional];
		}
		return [...base, conditional];
	}

	onKindChange(fieldKey: string): void {
		// nichts zu tun — ngModel aktualisiert die Config direkt
	}

	private isConfigComplete(field: BatchEditField, cfg: OperationConfig): boolean {
		// Where-Bedingungen: alle müssen ein Feld haben; je nach Operator zusätzlich Wert(e).
		for (const w of cfg.whereConditions) {
			if (!w.field) return false;
			if (opNeedsNoValue(w.op)) continue;
			if (w.value === undefined || w.value === '') return false;
			if (opNeedsTwoValues(w.op) && (w.value2 === undefined || w.value2 === '')) return false;
		}
		// Sonderfall Schicht-fuer-Art: zusaetzlich Pattern + Zielschicht erforderlich.
		if (field.key === SCHICHT_FOR_ART_FIELD_KEY) {
			if (cfg.kind !== 'set') return false;
			if (!cfg.setValue) return false;
			if (!cfg.artPattern || compileArtPattern(cfg.artPattern) === null) return false;
			return true;
		}
		switch (cfg.kind) {
			case 'set': return cfg.setValue !== undefined;
			case 'clear': return true;
			case 'mapping':
				if (!cfg.mappingText || cfg.mappingText.trim().length === 0) return false;
				// Bei enum-Zielfeldern muss jeder „neu"-Wert in der Liste der erlaubten Werte
				// stehen — sonst wuerden ueber das Mapping unzulaessige Werte ins Dokument
				// gelangen, die das Dropdown-Feld eigentlich nicht akzeptiert. Cache wird
				// via (ngModelChange) -> onMappingTextChange aktuell gehalten.
				if (cfg.mappingInvalidTargets.length > 0) return false;
				return true;
			case 'replaceList':
				return !!cfg.replaceListText && parseReplaceRules(cfg.replaceListText).length > 0;
			case 'stringTransform': {
				if (!cfg.stringTransform) return false;
				if (cfg.stringTransform === 'replace' && !cfg.strArg1) return false;
				if ((cfg.stringTransform === 'prefix' || cfg.stringTransform === 'suffix') && !cfg.strArg1) return false;
				return true;
			}
			case 'arithmetic':
				return !!cfg.arithmeticOp && cfg.arithmeticValue !== undefined && !Number.isNaN(cfg.arithmeticValue);
			case 'dateOp':
				if (!cfg.dateOp) return false;
				if (cfg.dateOp === 'addDays' && (cfg.dateDays === undefined || Number.isNaN(cfg.dateDays))) return false;
				return true;
			case 'ifThen':
				if (!cfg.ifCondOp || !cfg.ifThenKind) return false;
				if (!opNeedsNoValue(cfg.ifCondOp)) {
					if (cfg.ifCondValue === undefined || cfg.ifCondValue === '') return false;
					if (opNeedsTwoValues(cfg.ifCondOp) && (cfg.ifCondValue2 === undefined || cfg.ifCondValue2 === '')) return false;
				}
				if (cfg.ifThenKind === 'set' && cfg.ifThenValue === undefined) return false;
				return true;
			default: return false;
		}
	}

	private buildOperation(field: BatchEditField, cfg: OperationConfig): BatchEditOperation | null {
		// Sonderfall Schicht-fuer-Art: erzwingt onlyIfEmpty=true. Der Accessor signalisiert
		// per getValue=null, ob ein Punkt aenderungsfaehig ist; ohne onlyIfEmpty waeren alle
		// Punkte als "geaendert" markiert und der Sentinel-Skip wuerde uebergangen.
		const forceOnlyIfEmpty = field.key === SCHICHT_FOR_ART_FIELD_KEY;
		const onlyIfEmpty = (forceOnlyIfEmpty || cfg.onlyIfEmpty) || undefined;
		const where = this.buildWhere(cfg);
		const base = { onlyIfEmpty, where };
		switch (cfg.kind) {
			case 'set':
				return { kind: 'set', value: this.coerceValue(field, cfg.setValue), ...base };
			case 'clear':
				return { kind: 'clear', ...base };
			case 'mapping':
				return {
					kind: 'mapping',
					map: parseMappingText(cfg.mappingText ?? ''),
					passthrough: cfg.mappingPassthrough,
					...base
				};
			case 'replaceList':
				return {
					kind: 'replaceList',
					rules: parseReplaceRules(cfg.replaceListText ?? ''),
					regex: cfg.replaceListRegex || undefined,
					...base
				};
			case 'stringTransform':
				return {
					kind: 'stringTransform',
					transform: cfg.stringTransform!,
					prefix: cfg.stringTransform === 'prefix' ? cfg.strArg1 : undefined,
					suffix: cfg.stringTransform === 'suffix' ? cfg.strArg1 : undefined,
					from: cfg.stringTransform === 'replace' ? cfg.strArg1 : undefined,
					to: cfg.stringTransform === 'replace' ? cfg.strArg2 : undefined,
					regex: cfg.stringTransform === 'replace' ? cfg.strRegex : undefined,
					...base
				};
			case 'arithmetic':
				return { kind: 'arithmetic', op: cfg.arithmeticOp!, value: cfg.arithmeticValue!, ...base };
			case 'dateOp':
				return { kind: 'dateOp', op: cfg.dateOp!, days: cfg.dateDays, ...base };
			case 'ifThen': {
				const condition = this.buildIfThenCondition(field, cfg);
				const then = cfg.ifThenKind === 'clear'
					? ({ kind: 'clear' } as const)
					: ({ kind: 'set', value: cfg.ifThenValue ?? '' } as const);
				return { kind: 'ifThen', condition, then, ...base };
			}
			default:
				return null;
		}
	}

	/**
	 * Wandelt die UI-Where-Liste in das BatchEditOperation-Format. Leeres Array → undefined.
	 * Werte werden je nach Typ des Where-Felds koerziert (Number-Felder: Number, sonst String) —
	 * sonst scheitert ein numerischer Vergleich, wenn der Doc-Wert eine Zahl ist und der UI-Wert
	 * als String reinkommt (5 lt "10" wäre lex statt numerisch).
	 */
	private buildWhere(cfg: OperationConfig): WhereCondition[] | undefined {
		if (!cfg.whereConditions || cfg.whereConditions.length === 0) return undefined;
		return cfg.whereConditions.map(w => {
			if (opNeedsNoValue(w.op)) {
				return { field: w.field, op: w.op as 'empty' | 'nonEmpty' };
			}
			const targetField = this.fields.find(f => f.key === w.field);
			const v1 = targetField ? this.coerceValue(targetField, w.value) : (w.value ?? '');
			if (opNeedsTwoValues(w.op)) {
				const v2 = targetField ? this.coerceValue(targetField, w.value2) : (w.value2 ?? '');
				return { field: w.field, op: 'between', value: v1, value2: v2 };
			}
			return { field: w.field, op: w.op as 'eq' | 'ne' | 'contains' | 'notContains' | 'lt' | 'lte' | 'gt' | 'gte', value: v1 };
		});
	}

	/**
	 * Wandelt die UI-IfThen-Konfig in eine `IfThenCondition` (Lib-Typ).
	 * Wert(e) werden je nach Zielfeld-Typ koerziert — siehe `buildWhere` für die Begründung.
	 */
	private buildIfThenCondition(field: BatchEditField, cfg: OperationConfig): IfThenCondition {
		const op = cfg.ifCondOp!;
		if (opNeedsNoValue(op)) return { op: op as 'empty' | 'nonEmpty' };
		const v1 = this.coerceValue(field, cfg.ifCondValue);
		if (opNeedsTwoValues(op)) {
			const v2 = this.coerceValue(field, cfg.ifCondValue2);
			return { op: 'between', value: v1, value2: v2 };
		}
		return { op: op as 'eq' | 'ne' | 'contains' | 'notContains' | 'lt' | 'lte' | 'gt' | 'gte', value: v1 };
	}

	// Template-Helper für die Operator-Dropdowns / Wert-Inputs ----------------------

	/** Operator-Liste für eine Where-Bedingung — abhängig vom dort gewählten Feld. */
	whereOpOptions(whereFieldKey: string): { value: ConditionOpUi; label: string }[] {
		const field = this.fields.find(f => f.key === whereFieldKey);
		return conditionOpsForType(field?.type);
	}

	/** Operator-Liste für die IfThen-Bedingung — abhängig vom Zielfeld-Typ. */
	ifThenOpOptions(field: BatchEditField): { value: ConditionOpUi; label: string }[] {
		return conditionOpsForType(field.type);
	}

	opNeedsNoValue(op: ConditionOpUi | undefined): boolean { return opNeedsNoValue(op); }
	opNeedsTwoValues(op: ConditionOpUi | undefined): boolean { return opNeedsTwoValues(op); }

	/**
	 * Liefert die zulaessigen Enum-Werte fuer einen Feld-Key, oder null wenn das Feld
	 * kein enum ist (oder unbekannt). Dient dem Template, um in Where-Bedingungen,
	 * IfThen-Bedingungen und IfThen-Ziel-Werten ein Dropdown statt eines freien
	 * Text-Inputs zu rendern, sobald sich das Bezugsfeld als enum entpuppt.
	 *
	 * Returnt eine stabile Referenz (das in BATCH_EDIT_FIELDS hinterlegte Array),
	 * damit das Template die Methode in *ngIf="...as opts" nutzen kann, ohne dass
	 * Angular bei jedem CD-Pass eine neue Identitaet sieht (NG0103).
	 */
	enumValuesForKey(key: string | undefined): BatchEditFieldEnumValue[] | null {
		if (!key) return null;
		const f = this.fields.find(x => x.key === key);
		if (!f || f.type !== 'enum' || !f.enumValues) return null;
		return f.enumValues;
	}

	/**
	 * Berechnet die unzulaessigen Mapping-Ziel-Werte (`neu`-Seite) fuer enum-Felder neu
	 * und cached die Liste auf der Config. Aufruf via (ngModelChange) am Mapping-Textarea
	 * — danach kann das Template `cfg.mappingInvalidTargets` ohne Methoden-Aufruf lesen,
	 * was NG0103 (Endlos-CD) durch wechselnde Array-Identitaet vermeidet.
	 *
	 * Bei Nicht-Enum-Feldern bleibt die Liste leer, sodass die Validierungs-Branche
	 * im Template nichts anzeigt und isConfigComplete nicht blockiert.
	 */
	onMappingTextChange(field: BatchEditField, fieldKey: string): void {
		const cfg = this.getConfig(fieldKey);
		if (field.type !== 'enum' || !field.enumValues || !cfg.mappingText) {
			cfg.mappingInvalidTargets = [];
			return;
		}
		const allowed = new Set(field.enumValues.map(e => String(e.value)));
		const map = parseMappingText(cfg.mappingText);
		const bad: string[] = [];
		for (const v of Object.values(map)) {
			if (!allowed.has(v)) bad.push(v);
		}
		cfg.mappingInvalidTargets = Array.from(new Set(bad));
	}

	private coerceValue(field: BatchEditField, raw: string | undefined): string | number | boolean | null {
		if (raw === undefined || raw === '') return field.type === 'string' ? '' : null;
		if (field.type === 'number') {
			const n = Number(raw);
			return Number.isFinite(n) ? n : null;
		}
		if (field.type === 'boolean') return raw === 'true';
		return raw;
	}

	// -------------------------------------------------------------------
	// Step 4: Preview
	// -------------------------------------------------------------------

	private computePreview(): void {
		// Bestand-Konvertierung: Trockenlauf-Plan + Vorschau-Zeilen (alt→neu).
		const canWrite = (p: AufnahmePunktDocument) => this.workflow.canEditPoint(p);
		this.bestandPlan = this.bestandKonvertieren ? planBestandBatch(this.selectedPoints, canWrite) : null;
		this.bestandPreviewRows = this.bestandKonvertieren ? previewBestandBatch(this.selectedPoints, canWrite) : [];

		const changes: PlannedChange[] = [];
		for (const field of this.selectedFieldsList) {
			const cfg = this.configs.get(field.key);
			if (!cfg) continue;
			const op = this.buildOperation(field, cfg);
			if (op) changes.push({ field, operation: op });
		}
		// Modul-Kontext fuer den "Schicht fuer Pflanzenart"-Accessor setzen, damit
		// dessen getValue/setValue dasselbe Muster sehen wie der Wizard. In `finally`
		// raeumen — sonst wuerde ein nachfolgendes Berechnen ohne Sonder-Feld noch
		// auf das alte Muster zugreifen.
		this.applyArtPatternContext();
		try {
			this.preview = this.batchEditService.computePreview(
				this.selectedPoints,
				changes,
				(p: AufnahmePunktDocument) => p.header?.nummer ?? p._id
			);
		} finally {
			setActiveArtPattern(null);
		}
		rewriteSchichtForArtSkipReasons(this.preview);
		this.excludedPreviewPointIds.clear();
		this.refreshActivePreview();
	}

	/**
	 * Setzt den Modul-Kontext aus dem Sonder-Feld-Cfg, falls es ausgewaehlt ist.
	 * Wird vor computePreview / runExecute aufgerufen; Aufrufer ist fuer Cleanup
	 * (`setActiveArtPattern(null)`) verantwortlich.
	 */
	private applyArtPatternContext(): void {
		const cfg = this.configs.get(SCHICHT_FOR_ART_FIELD_KEY);
		if (!cfg || !this.selectedFieldKeys.has(SCHICHT_FOR_ART_FIELD_KEY)) {
			setActiveArtPattern(null);
			return;
		}
		const re = compileArtPattern(cfg.artPattern ?? '');
		const target = cfg.setValue;
		if (!re || !target) {
			setActiveArtPattern(null);
			return;
		}
		setActiveArtPattern({ regex: re, targetSchicht: target as 'b1' | 'b2' | 'str' | 'kr1' | 'kr2' | 'm' });
	}

	togglePreviewRow(pointId: string): void {
		if (this.excludedPreviewPointIds.has(pointId)) this.excludedPreviewPointIds.delete(pointId);
		else this.excludedPreviewPointIds.add(pointId);
		this.refreshActivePreview();
	}

	formatValue(v: unknown): string {
		if (v === null || v === undefined || v === '') return '(leer)';
		return String(v);
	}

	// -------------------------------------------------------------------
	// Step 5: Ausfuehrung
	// -------------------------------------------------------------------

	private async runExecute(): Promise<void> {
		this.executing = true;
		this.step = 'ausfuehrung';
		this.progressDone = 0;
		this.currentPointId = '';

		// Nur Punkte ans executeBatch geben, die im Preview tatsaechlich aenderungsfaehig
		// (`willChange`) und nicht manuell deaktiviert sind. Punkte ohne Aenderung wuerden
		// im Service ohnehin nur unchangedPoints++ ausloesen, blaehen aber den Progressbar-
		// Total auf — der Nutzer sieht dann „250/500", obwohl nur 250 Punkte angefasst werden.
		const willChangeIds = new Set(this.preview.filter(r => r.willChange).map(r => r.pointId));
		const pointsToSave = this.selectedPoints.filter(p =>
			!this.excludedPreviewPointIds.has(p._id) && willChangeIds.has(p._id));
		const rowsToSave = this.preview.filter(r =>
			!this.excludedPreviewPointIds.has(r.pointId) && r.willChange);
		this.progressTotal = pointsToSave.length;
		const label = this.buildBatchLabel();

		// Bestand-Konvertierung: Punkte vorab mutieren. Punkte, die ohnehin per
		// executeBatch (Feld-Op) gespeichert werden, tragen die Bestand-Änderung mit;
		// der Rest wird gebündelt (bulkDocs, IP-sicher) separat gespeichert.
		this.bestandGespeichert = 0;
		if (this.bestandKonvertieren) {
			const bestandPts = this.selectedPoints.filter(p => !this.excludedPreviewPointIds.has(p._id));
			const changed = runBestandBatch(bestandPts, p => this.workflow.canEditPoint(p));
			const feldGespeichert = new Set(pointsToSave.map(p => p._id));
			const nurBestand = changed.filter(p => !feldGespeichert.has(p._id));
			if (nurBestand.length) {
				try { await this.ds.bulkDocs(nurBestand); }
				catch (e: any) { this.toastr.error(e?.message || 'Bestand-Speichern fehlgeschlagen', 'Sammelbearbeitung'); }
			}
			this.bestandGespeichert = changed.length;
		}

		// Pattern-Kontext erneut setzen — `executeBatch` ruft setValue() pro Punkt,
		// und der Accessor liest das Pattern aus dem Modul-Kontext. `finally` raeumt
		// auch bei Abbruch/Exception auf.
		this.applyArtPatternContext();
		try {
			this.report = await this.batchEditService.executeBatch(
				pointsToSave,
				rowsToSave,
				{
					label,
					// G8: Pro-Punkt-Schreibrechts-Gate — nur Punkte, die der User im
					// aktuellen Workflow-Status auch einzeln editieren dürfte, werden
					// geschrieben; der Rest landet im Report unter „übersprungen".
					canWritePoint: (p: any) => this.workflow.canEditPoint(p),
					skipReason: 'keine Schreibrechte (Workflow-Status/Rolle)',
					onProgress: p => {
						this.progressDone = p.done;
						this.progressTotal = p.total;
						this.currentPointId = p.pointId;
					}
				}
			);
		} finally {
			setActiveArtPattern(null);
		}

		this.executing = false;
		this.step = 'abgeschlossen';
	}

	private buildBatchLabel(): string {
		const fields = this.selectedFieldsList.map(f => f.label).join(', ');
		const n = this.activePreview.filter(r => r.willChange).length;
		return `Batch-Edit: ${fields} (${n} Punkte)`;
	}

	// -------------------------------------------------------------------
	// Navigation
	// -------------------------------------------------------------------

	canGoNext(): boolean {
		switch (this.step) {
			case 'auswahl':      return this.selectedPoints.length > 0;
			case 'felder':       return this.selectedFieldKeys.size > 0 || this.bestandKonvertieren;
			case 'operationen':  return this.allConfigsValid;
			case 'preview':      return this.previewChangedCount > 0
				|| (this.bestandKonvertieren && (this.bestandPlan?.konvertierbar ?? 0) > 0);
			default:             return false;
		}
	}

	async goNext(): Promise<void> {
		switch (this.step) {
			case 'auswahl':      this.step = 'felder'; break;
			case 'felder':       this.step = 'operationen'; break;
			case 'operationen':  this.computePreview(); this.step = 'preview'; break;
			case 'preview':      await this.runExecute(); break;
			default:             break;
		}
	}

	goBack(): void {
		switch (this.step) {
			case 'felder':       this.step = 'auswahl'; break;
			case 'operationen':  this.step = 'felder'; break;
			case 'preview':      this.step = 'operationen'; break;
			default:             break;
		}
	}

	cancel(): void { this.modal.dismiss('cancel'); }

	close(): void {
		// Bestand-Konvertierung zählt zum „changedPoints", damit die Liste auch dann
		// neu lädt, wenn KEINE Feld-Operation lief (nur Bestand konvertiert wurde).
		const changedPoints = (this.report?.changedPoints ?? 0) + this.bestandGespeichert;
		this.modal.close({ ...(this.report ?? {}), changedPoints });
	}

	/**
	 * Schließt den Wizard und springt im Handbuch zur Regex-Anleitung.
	 * Aufruf aus der Inline-Regex-Hilfe heraus, damit der Nutzer nicht im Modal
	 * gefangen ist, wenn er die ausführliche Doku braucht.
	 */
	openHandbuchRegex(): void {
		this.modal.dismiss('open-handbuch');
		this.router.navigate(['/handbuch'], { fragment: 'batch-regex' });
	}

	/**
	 * Sackgasse aus dem Auswahl-Step heraus: schließt den Wizard und öffnet
	 * stattdessen den WMS-Progress-Modal mit denselben ausgewählten Punkten.
	 * Es gibt absichtlich keinen Rückweg in den Wizard — der Lauf ist eigenständig
	 * und das Wiederöffnen würde nur den Edit-Pfad mit dem WMS-Pfad vermischen.
	 */
	startWmsRefresh(): void {
		if (this.selectedPoints.length === 0) return;
		const points = this.selectedPoints;
		// Wizard zuerst schließen, damit es nicht zwei gestapelte Modals gibt
		this.modal.dismiss('switch-to-wms');
		const ref = this.modalService.open(BatchWmsProgressComponent, { size: 'lg', backdrop: 'static' });
		ref.componentInstance.points = points;
	}

	/**
	 * Zweiter Pfad aus dem Auswahl-Step: öffnet den Projekt-Workflow-Editor
	 * (Status-Übersicht). Dort steckt — über das Workflow-Diagramm hinter dem
	 * Status-Badge — der berechtigungsgeprüfte Bulk-Statuswechsel (Punkte
	 * sammelweise greifen / in den Pool zurückgeben / vorlegen / annehmen /
	 * abschließen …). Analog zu `startWmsRefresh()` eine Sackgasse: der Assistent
	 * wird geschlossen und der Editor läuft eigenständig weiter.
	 *
	 * Wir öffnen denselben Standalone-Editor wie der Projekt-Status-Badge
	 * (ProjectManager → onOpenWorkflowEditor) und bauen die gegateten Bulk-Aktionen
	 * bewusst NICHT nach — der frühere freie Bulk-Statuswechsel an dieser Stelle
	 * war irreführend.
	 */
	openWorkflowEditor(): void {
		this.modal.dismiss('switch-to-workflow');
		const ref = this.modalService.open(WorkflowEditorComponent, { scrollable: true });
		ref.componentInstance.standalone = true;
	}

	// Template-Helfer: Config lesbar in Templates
	getConfig(fieldKey: string): OperationConfig {
		let cfg = this.configs.get(fieldKey);
		if (!cfg) {
			cfg = makeDefaultConfig();
			this.configs.set(fieldKey, cfg);
		}
		return cfg;
	}

	/** Datumsfeld „Neuer Wert“: ein Klick irgendwo ins Feld oeffnet den Kalender. */
	oeffneKalender(feld: EventTarget | null): void {
		kalenderOeffnen(feld);
	}

	/** Knopf „Heute“ am Datumsfeld „Neuer Wert“: dasselbe Format wie eine Wahl im Kalender. */
	setzeHeute(fieldKey: string): void {
		this.getConfig(fieldKey).setValue = heutigesDatum();
	}

	/** True, wenn das Feld das Sonder-UI fuer Art-Muster + Moose-Preset bekommt. */
	isSchichtForArtField(fieldKey: string): boolean {
		return fieldKey === SCHICHT_FOR_ART_FIELD_KEY;
	}

	/**
	 * Befuellt das Art-Muster mit der „Moose"-Vorlage (gaengige Bryophyta-Genera) und
	 * setzt zugleich Zielschicht=m und kind='set'. Ein Klick reicht — wer Moose woandershin
	 * verschieben will, hat keinen sinnvollen Grund, dafuer den Moose-Preset zu nutzen.
	 */
	applyMoosPreset(fieldKey: string): void {
		const cfg = this.getConfig(fieldKey);
		cfg.artPattern = MOOS_GENERA_PRESET;
		cfg.setValue = 'm';
		cfg.kind = 'set';
	}

	// -------------------------------------------------------------------
	// Where-Bedingungen (Cross-Field)
	// -------------------------------------------------------------------

	/**
	 * Liste aller verfügbaren Felder für die Where-Bedingungs-Auswahl.
	 * Wird einmal beim Init befüllt — KEIN Getter, sonst neue Array-Identität pro
	 * Change-Detection-Pass und damit Endlos-CD im verschachtelten *ngFor (NG0103).
	 */
	whereFieldOptions: { key: string; label: string }[] = [];

	addWhere(fieldKey: string): void {
		const cfg = this.getConfig(fieldKey);
		cfg.whereConditions.push({ field: '', op: 'eq', value: '' });
	}

	removeWhere(fieldKey: string, index: number): void {
		const cfg = this.getConfig(fieldKey);
		cfg.whereConditions.splice(index, 1);
	}

	/** Wenn der Operator zu empty/nonEmpty wechselt: Wert-Felder leeren. */
	onWhereOpChange(fieldKey: string, index: number): void {
		const cfg = this.getConfig(fieldKey);
		const w = cfg.whereConditions[index];
		if (!w) return;
		if (opNeedsNoValue(w.op)) { w.value = ''; w.value2 = ''; }
		else if (!opNeedsTwoValues(w.op)) { w.value2 = ''; }
	}

	/**
	 * Wenn das Where-Feld geändert wird, kann der bisherige Operator für den neuen Feldtyp
	 * unzulässig sein (z.B. `contains` auf ein Number-Feld). Auf den ersten gültigen Operator
	 * der neuen Liste resetten — sonst zeigt das Dropdown ein leeres Auswahlfeld.
	 */
	onWhereFieldChange(fieldKey: string, index: number): void {
		const cfg = this.getConfig(fieldKey);
		const w = cfg.whereConditions[index];
		if (!w) return;
		const allowed = this.whereOpOptions(w.field).map(o => o.value);
		if (!allowed.includes(w.op)) {
			w.op = (allowed[0] ?? 'eq');
			this.onWhereOpChange(fieldKey, index);
		}
	}
}

// -------------------------------------------------------------------------
// Utility: Mapping-Text parsen
// -------------------------------------------------------------------------

/**
 * Parst Text-Input im Format "A = B" (eine Regel pro Zeile) in ein Mapping-Objekt.
 * Leere Zeilen und Zeilen ohne "=" werden ignoriert.
 */
function parseMappingText(text: string): Record<string, string> {
	const map: Record<string, string> = {};
	for (const line of text.split('\n')) {
		const idx = line.indexOf('=');
		if (idx < 0) continue;
		const k = line.substring(0, idx).trim();
		const v = line.substring(idx + 1).trim();
		if (k) map[k] = v;
	}
	return map;
}

